/**
 * Página /admin/consulta-vecinos — Consulta de vecinos (módulo Comisión).
 *
 * Permite ubicar a un vecino buscando por N° de casa, patente, RUT, nombre o
 * identificador de recurso (llavero / control), y ver la ficha completa de
 * la casa: personas con tenencia activa, residentes, vehículos, recursos y
 * resumen financiero. Solo lectura — no expone acciones de cobro ni
 * suspensión (esas viven en la vista Tesorería).
 *
 * RBAC: bajo el bloque ROLES_ADMIN del router; el backend exige
 * require_comision (el tesorero también accede porque su rol incluye
 * comisión, doctrina convención #14).
 *
 * Las funciones de datos se inyectan por props (con default a la API real)
 * para que el preview dev-only pueda alimentar mock data.
 */
import { useEffect, useState } from 'react';
import {
  buscarVecinos as buscarVecinosApi,
  getFichaVecino as getFichaVecinoApi,
  type CoincidenciaCasa,
  type FichaVecino,
  type MotivoMatch,
} from '../../lib/api/comision-vecinos';
import type { CasaAnualResumen } from '../../lib/api/casas-tesoreria';
import { ApiError } from '../../lib/api/client';
import { ETIQUETA_TIPO_TENENCIA } from '../../lib/api/types';
import { PARENTESCO_LABEL, TRACCION_LABEL } from '../../lib/labels';
import type { ParentescoLiteral } from '../../lib/api/residentes';
import type { TipoTraccion } from '../../lib/api/types';
import type { PortalEstado, RecursoFicha } from '../../lib/api/comision-vecinos';
import { formatCLP, formatCelular, formatFecha, formatRut } from '../../lib/format';
import { formatearUltimoAcceso, hoy, isoAFechaDisplay } from '../../lib/tiempo';
import { Badge } from '../../components/ui/Badge';
import {
  EstadoWifiInline,
  type EstadoWifi,
} from '../../components/ui/CirculoWifi';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { CeldaMes, ESTILO_MES, MES_CORTO } from '../../components/ui/CeldaMes';
import { EstadoCasaBadge } from '../../components/ui/EstadoCasaBadge';
import { EmptyState } from '../../components/ui/EmptyState';

/* ===== Etiquetas usuario-facing (convención #13) ===== */

const MOTIVO_LABEL: Record<MotivoMatch, string> = {
  casa: 'N° de casa',
  recurso: 'Llavero / control',
  patente: 'Patente',
  rut: 'RUT',
  nombre: 'Nombre',
};

const MOTIVO_VARIANT: Record<
  MotivoMatch,
  'pending' | 'success' | 'warning' | 'danger' | 'info'
> = {
  casa: 'info',
  recurso: 'info',
  patente: 'info',
  rut: 'success',
  nombre: 'pending',
};

const RECURSO_LABEL: Record<string, string> = {
  llavero: 'Llavero',
  control_remoto: 'Control remoto',
  otro: 'Otro',
};

function etiquetaTenencia(tipo: string): string {
  return ETIQUETA_TIPO_TENENCIA[tipo] ?? tipo;
}

function etiquetaParentesco(parentesco: string): string {
  return PARENTESCO_LABEL[parentesco as ParentescoLiteral] ?? parentesco;
}

function etiquetaRecurso(tipo: string): string {
  return RECURSO_LABEL[tipo] ?? tipo;
}

/* ===== Props (inyectables para el preview) ===== */

export interface ConsultaVecinosProps {
  buscarFn?: (q: string) => Promise<CoincidenciaCasa[]>;
  fichaFn?: (loteId: string) => Promise<FichaVecino>;
}

export function ConsultaVecinos({
  buscarFn = buscarVecinosApi,
  fichaFn = getFichaVecinoApi,
}: ConsultaVecinosProps = {}) {
  const [q, setQ] = useState('');
  const [coincidencias, setCoincidencias] = useState<CoincidenciaCasa[] | null>(
    null,
  );
  const [buscando, setBuscando] = useState(false);
  const [errorBusqueda, setErrorBusqueda] = useState<string | null>(null);

  const [loteSeleccionado, setLoteSeleccionado] = useState<string | null>(null);
  const [ficha, setFicha] = useState<FichaVecino | null>(null);
  const [cargandoFicha, setCargandoFicha] = useState(false);
  const [errorFicha, setErrorFicha] = useState<string | null>(null);

  // Búsqueda con debounce (350 ms). El backend exige q >= 2 caracteres.
  useEffect(() => {
    const termino = q.trim();
    if (termino.length < 2) {
      setCoincidencias(null);
      setBuscando(false);
      setErrorBusqueda(null);
      return;
    }
    let cancelado = false;
    setBuscando(true);
    setErrorBusqueda(null);
    const id = window.setTimeout(async () => {
      try {
        const r = await buscarFn(termino);
        if (!cancelado) setCoincidencias(r);
      } catch (e) {
        if (!cancelado) {
          setErrorBusqueda(
            e instanceof ApiError
              ? 'No se pudo completar la búsqueda. Intenta de nuevo.'
              : 'No se pudo completar la búsqueda. Intenta de nuevo.',
          );
          setCoincidencias(null);
        }
      } finally {
        if (!cancelado) setBuscando(false);
      }
    }, 350);
    return () => {
      cancelado = true;
      window.clearTimeout(id);
    };
  }, [q, buscarFn]);

  // Carga de la ficha al seleccionar una casa.
  useEffect(() => {
    if (!loteSeleccionado) {
      setFicha(null);
      setErrorFicha(null);
      return;
    }
    let cancelado = false;
    setCargandoFicha(true);
    setErrorFicha(null);
    setFicha(null);
    fichaFn(loteSeleccionado)
      .then((f) => {
        if (!cancelado) setFicha(f);
      })
      .catch((e) => {
        if (!cancelado) {
          setErrorFicha(
            e instanceof ApiError && e.status === 404
              ? 'No se encontró la ficha de esta casa.'
              : 'No se pudo cargar la ficha de la casa.',
          );
        }
      })
      .finally(() => {
        if (!cancelado) setCargandoFicha(false);
      });
    return () => {
      cancelado = true;
    };
  }, [loteSeleccionado, fichaFn]);

  return (
    <div className="px-4 md:px-8 py-6 space-y-6">
      <div>
        <h1 className="text-2xl font-medium text-text-primary">
          Consulta de vecinos
        </h1>
        <p className="text-sm text-text-secondary mt-1">
          Ubica a un vecino por N° de casa, patente, RUT, nombre o número de
          llavero / control y revisa todos los datos de la casa. Solo consulta.
        </p>
      </div>

      <div>
        <label htmlFor="consulta-q" className="sr-only">
          Buscar vecino
        </label>
        <input
          id="consulta-q"
          type="text"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setLoteSeleccionado(null);
          }}
          placeholder="N° de casa, patente, RUT, nombre o llavero/control…"
          autoComplete="off"
          className="w-full bg-bg-card border-[0.5px] border-white/[0.12] rounded-md px-4 py-3 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-orange/60"
        />
      </div>

      {loteSeleccionado ? (
        <FichaCasa
          ficha={ficha}
          cargando={cargandoFicha}
          error={errorFicha}
          onVolver={() => setLoteSeleccionado(null)}
        />
      ) : (
        <Resultados
          q={q}
          buscando={buscando}
          error={errorBusqueda}
          coincidencias={coincidencias}
          onSeleccionar={setLoteSeleccionado}
        />
      )}
    </div>
  );
}

/* ===== Resultados de búsqueda ===== */

function Resultados({
  q,
  buscando,
  error,
  coincidencias,
  onSeleccionar,
}: {
  q: string;
  buscando: boolean;
  error: string | null;
  coincidencias: CoincidenciaCasa[] | null;
  onSeleccionar: (loteId: string) => void;
}) {
  if (q.trim().length < 2) {
    return (
      <p className="text-sm text-text-muted">
        Escribe al menos 2 caracteres para buscar.
      </p>
    );
  }
  if (buscando) {
    return <p className="text-sm text-text-muted">Buscando…</p>;
  }
  if (error) {
    return <p className="text-sm text-state-danger">{error}</p>;
  }
  if (!coincidencias || coincidencias.length === 0) {
    return (
      <EmptyState
        title="Sin coincidencias"
        description="No se encontró ninguna casa con ese término. Prueba con otro dato (N° de casa, patente, RUT, nombre o N° de llavero/control)."
      />
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-text-muted">
        {coincidencias.length}{' '}
        {coincidencias.length === 1
          ? 'casa encontrada'
          : 'casas encontradas'}
      </p>
      <ul className="space-y-2">
        {coincidencias.map((c) => (
          <li key={c.lote_id}>
            <button
              type="button"
              onClick={() => onSeleccionar(c.lote_id)}
              className="w-full text-left bg-bg-card border-[0.5px] border-white/[0.08] rounded-md px-4 py-3 hover:border-white/[0.24] transition-colors duration-base flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-base font-medium text-text-primary">
                    Casa {c.identificador}
                  </span>
                  {c.sector && (
                    <span className="text-xs text-text-muted">{c.sector}</span>
                  )}
                </div>
                <div className="text-sm text-text-secondary truncate mt-0.5">
                  {c.titular_nombre || 'Sin titular registrado'}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <Badge variant={MOTIVO_VARIANT[c.motivo_match]}>
                  {MOTIVO_LABEL[c.motivo_match]}
                </Badge>
                {c.detalle_match && (
                  <span className="text-[11px] text-text-muted">
                    {c.detalle_match}
                  </span>
                )}
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ===== Ficha de la casa ===== */

function FichaCasa({
  ficha,
  cargando,
  error,
  onVolver,
}: {
  ficha: FichaVecino | null;
  cargando: boolean;
  error: string | null;
  onVolver: () => void;
}) {
  // Recursos "en bolsa": ni asignados a una persona ni ligados a un vehículo.
  // Los asignados ya se muestran bajo su persona (llaveros) o su vehículo (CR).
  const recursosSinAsignar = ficha
    ? ficha.recursos.filter((r) => !r.persona_id && !r.vehiculo_patente)
    : [];
  return (
    <div className="space-y-4">
      {/* Toolbar (no se imprime): volver + imprimir/PDF. */}
      <div className="no-imprimir flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onVolver}
          className="text-sm text-text-secondary hover:text-text-primary"
        >
          ← Volver a los resultados
        </button>
        {ficha && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => window.print()}
            title="Se guarda como PDF desde el diálogo de impresión"
          >
            Imprimir / Guardar PDF
          </Button>
        )}
      </div>

      {cargando && <p className="text-sm text-text-muted">Cargando ficha…</p>}
      {error && <p className="text-sm text-state-danger">{error}</p>}

      {ficha && (
        <div className="area-imprimible space-y-4">
          {/* Membrete de impresión/PDF (oculto en pantalla). Tinta sobre
           * blanco a propósito: es un artefacto para papel, no la UI de tema
           * oscuro. Mismo lenguaje que los reportes de Tesorería. */}
          <div className="solo-impresion" style={{ marginBottom: 16 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                borderBottom: '2px solid #1a1a1a',
                paddingBottom: 10,
              }}
            >
              <div
                style={{
                  background: '#1a1a1a',
                  borderRadius: 8,
                  padding: 6,
                  display: 'flex',
                  flexShrink: 0,
                }}
              >
                <img
                  src="/escudo-calbucura-original.png"
                  alt="Escudo Barrio Calbucura"
                  style={{ height: 44, width: 'auto', display: 'block' }}
                />
              </div>
              <div>
                <div
                  style={{
                    fontSize: 11,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: '#555',
                  }}
                >
                  Comisión Barrio Calbucura
                </div>
                <div
                  style={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a' }}
                >
                  Ficha de vecino — Casa {ficha.casa.identificador}
                </div>
              </div>
              <div
                style={{
                  marginLeft: 'auto',
                  textAlign: 'right',
                  fontSize: 11,
                  color: '#555',
                }}
              >
                Emitido el {isoAFechaDisplay(hoy())}
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-baseline gap-3 flex-wrap">
              <h2 className="text-xl font-medium text-text-primary">
                Casa {ficha.casa.identificador}
              </h2>
              {ficha.casa.sector && (
                <span className="text-sm text-text-muted">
                  {ficha.casa.sector}
                </span>
              )}
            </div>
            <div className="text-sm text-text-secondary mt-0.5">
              {ficha.casa.titular_nombre || 'Sin titular registrado'}
            </div>
          </div>

          <BloqueFinanciero
            casa={ficha.casa}
            estadoPago={ficha.estado_pago}
            suspendida={ficha.suspendida}
          />

          <Card title="Titulares / responsables">
            {ficha.personas.length === 0 ? (
              <p className="text-sm text-text-muted">
                Sin personas con tenencia activa.
              </p>
            ) : (
              <ul className="divide-y divide-white/[0.06]">
                {ficha.personas.map((p) => (
                  <li
                    key={p.persona_id}
                    className="py-3 first:pt-0 last:pb-0 space-y-1"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-text-primary">
                        {p.nombre_completo}
                      </span>
                      <span className="text-xs text-text-muted">
                        {etiquetaTenencia(p.tipo_tenencia)}
                      </span>
                      {p.es_responsable_pago && (
                        <Badge variant="success">Responsable de pago</Badge>
                      )}
                    </div>
                    <BadgePortal
                      estado={p.portal_estado}
                      ultimoLogin={p.portal_ultimo_login}
                      onboardingCompletado={p.onboarding_completado}
                    />
                    <DatosContacto
                      rut={p.rut}
                      celular={p.celular}
                      email={p.email}
                    />
                    <AccesosDePersona
                      citofoniaSlot={p.citofonia_slot}
                      citofoniaEstado={p.citofonia_estado}
                      citofoniaProgramada={p.citofonia_programada}
                      wifiEstado={p.wifi_estado}
                      wifiEstadoEfectivo={p.wifi_estado_efectivo}
                    />
                    <LlaverosDePersona
                      recursos={ficha.recursos}
                      personaId={p.persona_id}
                    />
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Otros residentes">
            {ficha.residentes.length === 0 ? (
              <p className="text-sm text-text-muted">
                Sin otros residentes registrados.
              </p>
            ) : (
              <ul className="divide-y divide-white/[0.06]">
                {ficha.residentes.map((r) => (
                  <li
                    key={r.persona_id}
                    className="py-3 first:pt-0 last:pb-0 space-y-1"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-text-primary">
                        {r.nombre_completo}
                      </span>
                      <span className="text-xs text-text-muted">
                        {etiquetaParentesco(r.parentesco)}
                      </span>
                    </div>
                    {/* Solo si tiene algo que decir: la mayoría de los
                        residentes no tiene cuenta de portal (el rol vecino
                        cuelga de la tenencia), así que un "Sin cuenta" en
                        cada uno sería ruido. */}
                    {r.portal_estado !== 'sin_solicitud' && (
                      <BadgePortal
                        estado={r.portal_estado}
                        ultimoLogin={r.portal_ultimo_login}
                      />
                    )}
                    <DatosContacto
                      rut={r.rut}
                      celular={r.celular}
                      email={r.email}
                    />
                    <AccesosDePersona
                      citofoniaSlot={r.citofonia_slot}
                      citofoniaEstado={r.citofonia_estado}
                      citofoniaProgramada={r.citofonia_programada}
                      wifiEstado={r.wifi_estado}
                      wifiEstadoEfectivo={r.wifi_estado_efectivo}
                    />
                    <LlaverosDePersona
                      recursos={ficha.recursos}
                      personaId={r.persona_id}
                    />
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Vehículos">
            {ficha.vehiculos.length === 0 ? (
              <p className="text-sm text-text-muted">
                Sin vehículos registrados.
              </p>
            ) : (
              <ul className="divide-y divide-white/[0.06]">
                {ficha.vehiculos.map((v) => (
                  <li
                    key={v.patente}
                    className="py-3 first:pt-0 last:pb-0 space-y-0.5"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-medium text-text-primary tracking-wide">
                        {v.patente}
                      </span>
                      {v.asignado_a && (
                        <span className="text-sm text-text-secondary text-right">
                          {v.asignado_a}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-text-secondary">
                      {[
                        v.marca,
                        v.modelo,
                        v.color,
                        // Tracción solo si NO es combustión (relevante por
                        // baterías de litio; la común no se destaca).
                        v.tipo_traccion !== 'combustion'
                          ? TRACCION_LABEL[v.tipo_traccion as TipoTraccion]
                          : null,
                        v.control_remoto,
                      ]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Solo llaveros/CR en bolsa (sin asignar a persona ni vehículo).
           * Los asignados ya aparecen bajo su persona o su vehículo. */}
          {recursosSinAsignar.length > 0 && (
            <Card title="Llaveros y controles sin asignar">
              <ul className="divide-y divide-white/[0.06]">
                {recursosSinAsignar.map((rec) => (
                  <li
                    key={rec.identificador}
                    className="py-3 first:pt-0 last:pb-0 flex items-center justify-between gap-3"
                  >
                    <div>
                      <span className="text-sm font-medium text-text-primary">
                        {rec.identificador}
                      </span>
                      <span className="text-xs text-text-muted ml-2">
                        {etiquetaRecurso(rec.tipo)}
                      </span>
                      {rec.estado === 'reportado' && (
                        <span className="text-xs text-state-warning ml-2">
                          reportado
                        </span>
                      )}
                    </div>
                    {rec.serial && (
                      <span className="text-xs text-text-muted">
                        serial {rec.serial}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function FilaDato({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-text-muted">{label}</span>
      <span className="text-text-primary font-medium tabular-nums">
        {value}
      </span>
    </div>
  );
}

function BloqueFinanciero({
  casa,
  estadoPago,
  suspendida,
}: {
  casa: CasaAnualResumen;
  estadoPago: CasaAnualResumen['estado'];
  suspendida: boolean;
}) {
  const debe = casa.saldo_a_hoy > 0;
  const favor = casa.saldo_a_hoy < 0;
  const arrastre = casa.deuda_anio_anterior - casa.abono_anio_anterior;
  const anio = hoy().slice(0, 4);
  const saldoTexto = debe
    ? formatCLP(casa.saldo_a_hoy)
    : favor
      ? `−${formatCLP(-casa.saldo_a_hoy)} a favor`
      : 'Al día';
  return (
    <Card title="Estado de cuenta">
      <div className="space-y-2">
        {/* Estado de pago + (si aplica) suspendida + saldo neto a hoy. */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-1.5 flex-wrap">
            <EstadoCasaBadge estado={estadoPago} />
            {suspendida && <EstadoCasaBadge estado="suspendida" />}
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="text-[10px] uppercase tracking-wide text-text-muted">
              Saldo a hoy
            </span>
            <span
              className={`text-lg font-medium tabular-nums ${
                debe
                  ? 'text-state-danger'
                  : favor
                    ? 'text-state-success'
                    : 'text-text-secondary'
              }`}
            >
              {saldoTexto}
            </span>
          </div>
        </div>

        {/* Calendario de pago: título + leyenda inline, luego la fila de meses */}
        <div>
          <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
            <span className="text-[10px] uppercase tracking-wide text-text-muted">
              Calendario de pago {anio}
            </span>
            <div className="flex flex-wrap gap-x-2.5 gap-y-0.5">
              {(Object.keys(ESTILO_MES) as (keyof typeof ESTILO_MES)[]).map(
                (k) => (
                  <span key={k} className="flex items-center gap-1 text-[9px]">
                    <CeldaMes estado={k} small />
                    <span className="text-text-muted">
                      {ESTILO_MES[k].label}
                    </span>
                  </span>
                ),
              )}
            </div>
          </div>
          <div className="flex items-end gap-1">
            {MES_CORTO.map((m, i) => (
              <div key={i} className="flex flex-col items-center gap-0.5">
                <span className="text-[9px] text-text-muted">{m}</span>
                <CeldaMes estado={casa.meses[i] ?? 'futuro'} />
              </div>
            ))}
          </div>
        </div>

        {/* Desglose en 2 columnas para bajar altura */}
        <div className="border-t border-white/[0.06] pt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
          <FilaDato
            label="Deuda semestre actual"
            value={formatCLP(casa.saldo_semestre_actual)}
          />
          <FilaDato
            label="Saldo a favor"
            value={casa.saldo_a_favor > 0 ? formatCLP(casa.saldo_a_favor) : '—'}
          />
          {arrastre !== 0 && (
            <FilaDato
              label="Arrastre año anterior"
              value={formatCLP(arrastre)}
            />
          )}
          <FilaDato
            label="Último pago"
            value={
              casa.ultimo_pago_fecha
                ? formatFecha(casa.ultimo_pago_fecha)
                : 'Sin pagos registrados'
            }
          />
        </div>
      </div>
    </Card>
  );
}

function DatosContacto({
  rut,
  celular,
  email,
}: {
  rut: string | null;
  celular: string | null;
  email?: string | null;
}) {
  const partes: string[] = [];
  if (rut) partes.push(formatRut(rut));
  if (celular) partes.push(formatCelular(celular));
  if (email) partes.push(email);
  if (partes.length === 0) {
    return <div className="text-xs text-text-muted">Sin datos de contacto</div>;
  }
  return (
    <div className="text-xs text-text-secondary flex flex-wrap gap-x-3 gap-y-1">
      {partes.map((p) => (
        <span key={p}>{p}</span>
      ))}
    </div>
  );
}

function BadgePortal({
  estado,
  ultimoLogin,
  onboardingCompletado,
}: {
  estado: PortalEstado;
  ultimoLogin: string | null;
  onboardingCompletado?: boolean;
}) {
  // `cuenta_sin_ingresar` es el caso que hay que poder ver de un vistazo:
  // la comisión aprobó, la cuenta existe y el vecino nunca entró. Va en
  // warning porque es trabajo pendiente (hay que ayudarlo a entrar), no
  // un error del sistema.
  const config: Record<
    PortalEstado,
    { texto: string; variant: 'success' | 'warning' | 'danger' | 'info' }
  > = {
    activo: { texto: 'Portal activo', variant: 'success' },
    cuenta_sin_ingresar: { texto: 'Nunca ha entrado', variant: 'warning' },
    solicitud_pendiente: { texto: 'Solicitud pendiente', variant: 'warning' },
    solicitud_rechazada: { texto: 'Solicitud rechazada', variant: 'info' },
    // Aprobada pero sin cuenta: quedó a medio provisionar. Es trabajo
    // pendiente de la Comisión, no del vecino.
    aprobada_sin_cuenta: { texto: 'Aprobada sin cuenta', variant: 'danger' },
    cuenta_inactiva: { texto: 'Cuenta inactiva', variant: 'danger' },
    sin_solicitud: { texto: 'Sin cuenta', variant: 'info' },
  };
  const { texto, variant } = config[estado];

  return (
    <span className="inline-flex items-center gap-2 flex-wrap">
      <Badge variant={variant}>{texto}</Badge>
      {ultimoLogin && (
        <span className="text-xs text-text-muted">
          último acceso: {formatearUltimoAcceso(ultimoLogin)}
        </span>
      )}
      {onboardingCompletado === false && estado !== 'sin_solicitud' && (
        <span className="text-xs text-state-warning">onboarding pendiente</span>
      )}
    </span>
  );
}

function AccesosDePersona({
  citofoniaSlot,
  citofoniaEstado,
  citofoniaProgramada,
  wifiEstado,
  wifiEstadoEfectivo,
}: {
  citofoniaSlot: number | null;
  citofoniaEstado: string | null;
  citofoniaProgramada: boolean;
  wifiEstado: string | null;
  /** Cruce de los tres ejes; lo calcula el backend. */
  wifiEstadoEfectivo: string;
}) {
  if (!citofoniaEstado && !wifiEstado) return null;
  // Citofonía: "por programar" = validado pero todavía sin teclear en la
  // central. Sigue siendo espejo del HW, así que este pendiente es real y
  // vive en la pantalla Reprogramación.
  const pendienteCito = (
    <span className="text-state-warning"> · por programar</span>
  );
  return (
    <div className="text-xs text-text-secondary flex flex-wrap gap-x-3 gap-y-1">
      {citofoniaEstado && (
        <span>
          Citofonía{' '}
          <span className="font-medium text-text-primary">
            slot {citofoniaSlot}
          </span>
          {citofoniaEstado === 'pendiente_validacion' && (
            <span className="text-text-muted"> · por validar</span>
          )}
          {citofoniaEstado === 'activo' && !citofoniaProgramada && pendienteCito}
        </span>
      )}
      {/* Wifi: el mismo semáforo que usa Tesorería. Antes pintaba dos
          estados y no distinguía "el vecino no lo quiso" (gris) de "el
          sistema se lo cortó por morosidad" (rojo). */}
      {wifiEstado && (
        <span className="inline-flex items-center gap-1">
          <span className="font-medium text-text-primary">Wifi</span>
          <EstadoWifiInline
            estado={wifiEstadoEfectivo as EstadoWifi}
            mostrarSinSolicitar
          />
        </span>
      )}
    </div>
  );
}

/** Llaveros que porta una persona (nombre + serial). Los CR asignados a un
 *  vehículo NO se listan acá — aparecen en la línea del vehículo. */
function LlaverosDePersona({
  recursos,
  personaId,
}: {
  recursos: RecursoFicha[];
  personaId: string;
}) {
  const propios = recursos.filter(
    (r) => r.persona_id === personaId && !r.vehiculo_patente,
  );
  if (propios.length === 0) return null;
  return (
    <div className="text-xs text-text-secondary flex flex-wrap gap-x-3 gap-y-1">
      {propios.map((r) => (
        <span key={r.identificador}>
          {etiquetaRecurso(r.tipo)}{' '}
          <span className="font-medium text-text-primary">
            {r.identificador}
          </span>
          {r.serial && (
            <span className="text-text-muted"> · serial {r.serial}</span>
          )}
          {r.estado === 'reportado' && (
            <span className="text-state-warning"> · reportado</span>
          )}
        </span>
      ))}
    </div>
  );
}
